import { StyleSheet } from "react-native";

import {
  fonts,
  palette,
  radii,
  spacing,
  typography,
  type ThemeColors,
} from "~/styles/theme";

export const sidebarInset = spacing.m;

export const makeStylesheet = (colors: ThemeColors) =>
  StyleSheet.create({
    appShell: {
      flex: 1,
      flexDirection: "row",
      backgroundColor: colors.background,
    },
    appShellContent: { flex: 1, minWidth: 0 },
    upgradeBanner: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.s,
      paddingHorizontal: spacing.l,
      paddingVertical: spacing.s,
      backgroundColor: colors.dangerSurface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    sidebar: {
      width: 252,
      padding: spacing.l,
      backgroundColor: colors.background,
      borderRightWidth: 1,
      borderRightColor: colors.border,
    },
    sidebarBody: { flex: 1, gap: spacing.s },
    sidebarBrand: {
      paddingHorizontal: spacing.xs,
      marginBottom: spacing.l,
    },
    syncPill: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.s,
      marginBottom: spacing.m,
      paddingHorizontal: spacing.m,
      paddingVertical: spacing.s,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.elevated,
    },
    syncDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: colors.primary,
    },
    syncDotPending: { backgroundColor: palette.brandCoral },
    syncDotError: { backgroundColor: colors.danger },
    syncCopy: { flex: 1, minWidth: 0 },
    syncLabel: {
      ...typography.label,
      color: colors.heading,
    },
    syncHint: {
      ...typography.xs,
      color: colors.mutedForeground,
    },
    sidebarDivider: {
      paddingTop: spacing.m,
      marginTop: spacing.m,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    navItem: {
      minHeight: 42,
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.m,
      paddingHorizontal: sidebarInset,
      borderRadius: radii.m,
      borderWidth: 1,
      borderColor: "transparent",
    },
    navItemActive: {
      backgroundColor: colors.elevated,
      borderColor: colors.border,
    },
    navItemHover: { backgroundColor: colors.surface },
    navItemText: {
      ...typography.body,
      fontFamily: fonts.medium,
      color: colors.mutedForeground,
    },
    navItemTextActive: { color: colors.heading, fontFamily: fonts.semibold },
    sidebarUtilityLink: {
      minHeight: 36,
      justifyContent: "center",
      paddingHorizontal: sidebarInset,
      paddingVertical: 6,
      borderRadius: radii.m,
    },
    sidebarUtilityText: {
      ...typography.small,
      color: colors.mutedForeground,
    },
    sidebarUtilityTextActive: {
      color: colors.heading,
      fontFamily: fonts.medium,
    },
    utilityContent: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.s,
    },
    planBadge: {
      minWidth: 18,
      height: 18,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 6,
      borderRadius: 9,
      backgroundColor: colors.surface,
    },
    planBadgeText: {
      color: colors.mutedForeground,
      fontFamily: fonts.bold,
      fontSize: 10,
      letterSpacing: 0.4,
    },
    sidebarFooter: {
      gap: spacing.s,
      paddingTop: spacing.m,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    sidebarProfile: {
      minHeight: 44,
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingHorizontal: spacing.xs,
      borderRadius: radii.m,
    },
    sidebarAvatar: {
      width: 32,
      height: 32,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: radii.m,
      backgroundColor: colors.primary,
    },
    sidebarAvatarText: {
      color: colors.primaryForeground,
      fontFamily: fonts.bold,
      fontSize: 12,
    },
    sidebarProfileName: {
      ...typography.small,
      flex: 1,
      color: colors.heading,
      fontFamily: fonts.medium,
    },
    shellHeader: {
      minHeight: 56,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.s,
      paddingHorizontal: spacing.l,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      backgroundColor: colors.elevated,
    },
    shellHeaderLeft: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.s,
    },
    shellActions: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.s,
    },
    headerIconButton: {
      width: 36,
      height: 36,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.elevated,
    },
    headerIconButtonHover: { backgroundColor: colors.surface },
    breadcrumbs: {
      flex: 1,
      minWidth: 0,
      flexDirection: "row",
      alignItems: "center",
    },
    breadcrumbItem: { flexDirection: "row", alignItems: "center" },
    breadcrumbCurrent: {
      ...typography.small,
      flexShrink: 1,
      color: colors.heading,
      fontFamily: fonts.semibold,
    },
    breadcrumbSeparator: {
      ...typography.xs,
      color: colors.mutedForeground,
      marginHorizontal: spacing.xs,
    },
    drawerOverlay: {
      flex: 1,
      flexDirection: "row",
      backgroundColor: colors.overlay,
    },
    drawerPanel: {
      width: "82%",
      maxWidth: 320,
      padding: spacing.l,
      backgroundColor: colors.background,
    },
    drawerDismiss: { flex: 1 },
    modalOverlay: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.l,
      backgroundColor: colors.overlay,
    },
    confirmationCard: {
      width: "100%",
      maxWidth: 420,
      padding: spacing.xl,
      borderRadius: radii.card,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.elevated,
      gap: spacing.l,
    },
    modalTitle: {
      ...typography.display2,
      color: colors.heading,
    },
    confirmationActions: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: spacing.s,
    },
  });
